"use client"

import type React from "react"

import { useState } from "react"
import { useTranslations } from "next-intl"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { CheckCircle, Loader2 } from "lucide-react"

interface WaitlistModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function WaitlistModal({ open, onOpenChange }: WaitlistModalProps) {
  const t = useTranslations("pages.auth.waitlist")
  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    phone: "",
    role: "barber" as "owner" | "barber" | "both",
  })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [alreadyExists, setAlreadyExists] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.error || t("joinFailed"))
      }

      setAlreadyExists(data.alreadyExists || false)
      setSuccess(true)
      setTimeout(() => {
        onOpenChange(false)
        setSuccess(false)
        setAlreadyExists(false)
        setFormData({ email: "", full_name: "", phone: "", role: "barber" })
      }, 3000)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {success ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
            <DialogTitle className="text-2xl font-bold mb-2">
              {alreadyExists ? t("alreadyOnListTitle") : t("onListTitle")}
            </DialogTitle>
            <DialogDescription className="text-base">
              {alreadyExists ? t("alreadyOnListDesc") : t("onListDesc")}
            </DialogDescription>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{t("title")}</DialogTitle>
              <DialogDescription className="text-base">
                {t("subtitle")}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("emailLabel")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("emailPlaceholder")}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name">{t("fullNameLabel")}</Label>
                <Input
                  id="full_name"
                  type="text"
                  placeholder={t("fullNamePlaceholder")}
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t("phoneLabel")}</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder={t("phonePlaceholder")}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label>{t("interestedAs")}</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant={formData.role === "barber" ? "default" : "outline"}
                    onClick={() => setFormData({ ...formData, role: "barber" })}
                    disabled={loading}
                    className="w-full"
                  >
                    {t("barber")}
                  </Button>
                  <Button
                    type="button"
                    variant={formData.role === "owner" ? "default" : "outline"}
                    onClick={() => setFormData({ ...formData, role: "owner" })}
                    disabled={loading}
                    className="w-full"
                  >
                    {t("owner")}
                  </Button>
                  <Button
                    type="button"
                    variant={formData.role === "both" ? "default" : "outline"}
                    onClick={() => setFormData({ ...formData, role: "both" })}
                    disabled={loading}
                    className="w-full"
                  >
                    {t("both")}
                  </Button>
                </div>
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("joining")}
                  </>
                ) : (
                  t("join")
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
